import BaseComponent from '@utils/base.js';
import tag from '@utils/utilTags.js';
import audio from '@components/audioComponent/audio.js';

import * as styles from './nonogram.module.scss';

/** @extends {BaseComponent<HTMLDivElement>} */
export default class Nonogram extends BaseComponent {
  /** @param {Props<HTMLDivElement> & Required<{classes: string[], nonogram: number[][], isGameLoaded: boolean, difficulty: string}>} props */
  constructor(props) {
    super({ classes: [styles.nonogram, ...props.classes] });
    this.createNonogram(props.nonogram, props.isGameLoaded, props.difficulty);
    this.setCustomListeners();
  }

  /**
   * Creates a nonogram.
   * @param {number[][]} nonogram
   * @param {boolean} isGameLoaded
   * @param {string} difficulty
   * @returns {void}
   * @private
   */
  createNonogram(nonogram, isGameLoaded, difficulty) {
    const cellSizeClassPostfix =
      difficulty[0].toUpperCase() + difficulty.slice(1);

    nonogram.forEach((row, iY, solution) => {
      const rowTag = tag.div({ classes: [styles.row] });

      row.forEach((cell, iX) => {
        const cellTag = tag.div({
          classes: [styles.cell, styles[`cell${cellSizeClassPostfix}`]],
        });

        const cellElement = /** @type {CellElement} */ (cellTag.getElement());
        cellElement.customX = iX;
        cellElement.customY = iY;

        if (isGameLoaded) {
          cellElement.customValue = solution[iY][iX];
          if (cell === 1) cellTag.addClasses(styles.filled);
          if (cell === 2) cellTag.addClasses(styles.empty);
        } else {
          cellElement.customValue = 0;
        }

        rowTag.appendSingle(cellTag);
      });
      this.appendChildren(rowTag);
    });

    this.setClickListeners();
  }

  /**
   * Resets all marked cells.
   * @param {CustomEvent<{solution: number[][]}>} e
   * @returns {void}
   */
  resetNonogram = (e) => {
    const solution = e.detail && e.detail.solution;

    this.childComponents.forEach((row, iY) => {
      row.childComponents.forEach((cell, iX) => {
        cell.removeClasses(styles.filled, styles.empty);
        const cellElement = /** @type {CellElement} */ (cell.getElement());

        if (solution) {
          this.markCell(cellElement, solution[iY][iX]);
        }

        cellElement.customValue = 0;
      });
    });

    this.removeClickListeners();

    if (!solution) {
      this.setClickListeners();
    }
  };

  /**
   * Adds event listeners to custom controller events.
   * @returns {void}
   */
  setCustomListeners() {
    window.addEventListener(
      'custom:resetNonogram',
      /** @type {EventListener} */ (this.resetNonogram)
    );

    window.addEventListener('custom:win', this.removeClickListeners);

    window.addEventListener(
      'custom:showSolution',
      /** @type {EventListener} */ (this.resetNonogram)
    );
  }

  /**
   * Adds click event listeners to nonogram field.
   * @returns {void}
   */
  setClickListeners() {
    this.addListener('click', this.handleLeftClick);
    this.addListener('contextmenu', this.handleRightClick);
  }

  /**
   * Remove click event listeners from nonogram.
   * @returns {void}
   */
  removeClickListeners = () => {
    this.removeListener('click', this.handleLeftClick);
    this.removeListener('contextmenu', this.handleRightClick);
  };

  /**
   * Handles mouse button click (left or right).
   * @param {Event} e
   * @param {number[]} clickMap
   * @returns {void}
   * @private
   */
  handleCellClickOrContextMenu = (e, clickMap) => {
    const target = /** @type {Element} */ (e.target);
    const cell = /** @type {CellElement} */ (target.closest(`.${styles.cell}`));

    if (!cell) return;

    /** @type {MachineCustomEvent} */
    const machineGameEvent = new CustomEvent('custom:changestate', {
      detail: { action: 'game' },
    });

    window.dispatchEvent(machineGameEvent);

    const newValue = clickMap[cell.customValue];
    cell.customValue = newValue;

    this.markCell(cell, cell.customValue, true);

    /** @type {MachineCustomEvent} */
    const machineDrawEvent = new CustomEvent('custom:changestate', {
      detail: {
        action: 'draw',
        payload: {
          cell: {
            x: cell.customX,
            y: cell.customY,
            value: cell.customValue,
          },
        },
      },
    });

    window.dispatchEvent(machineDrawEvent);
  };

  /**
   * Handles left click.
   * @param {Event} e
   * @returns {void}
   * @private
   */
  handleLeftClick = (e) => {
    const clickMap = [1, 0, 1];
    this.handleCellClickOrContextMenu(e, clickMap);
  };

  /**
   * Handles right click.
   * @param {Event} e
   * @returns {void}
   * @private
   */
  handleRightClick = (e) => {
    e.preventDefault();
    const clickMap = [2, 2, 0];
    this.handleCellClickOrContextMenu(e, clickMap);
  };

  /**
   * Marks the cell.
   * @param {CellElement} cell
   * @param {number} value
   * @param {boolean} [isSound]
   * @returns {void}
   * @private
   */
  markCell(cell, value, isSound) {
    cell.classList.remove(styles.empty, styles.filled);

    switch (value) {
      case 0:
        isSound && audio.playNothing();
        break;

      case 1:
        cell.classList.add(styles.filled);
        isSound && audio.playFilled();
        break;

      case 2:
        cell.classList.add(styles.empty);
        isSound && audio.playEmpty();
        break;
    }
  }
}
